import { GradientButton } from '@components/Button';
import Icon from '@components/Icon/Icon';
import { DownloadIcon } from '@components/Icons/DownloadIcon';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { ForwardIcon } from '@components/Icons/ForwardIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import { PauseIcon } from '@components/Icons/PauseIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { PlaylistIcon } from '@components/Icons/PlaylistIcon';
import { PlayNextIcon } from '@components/Icons/PlayNextIcon';
import { PrevIcon } from '@components/Icons/PrevIcon';
import { NapsterSpinner } from '@components/NapsterSpinner/NapsterSpinner';
import { Tooltip } from '@components/Tooltip';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useFavorites } from '@hooks/useFavorites';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
//import { nyxId } from '../../../../config';
import { getContributorById } from '@services/api.ts';
import {
  trackContentShared,
  trackCreationDownloaded,
} from '@utils/analyticsHelpers';
import { extraQueryParams } from '@utils/constants';
import { getContentType } from '@utils/contentType';
import { sendSelectCompanionMessage } from '@utils/station';
import { placeHolderData } from '../../../../config';
import { SHARE_DESTINATIONS } from '../../../../constants/analytics';
import { AddToPlaylistModal } from '../AddToPlaylistModal';
import { LanguageSelector } from '../LanguageSelector';
import { PlaylistQueue } from '../PlaylistQueue';
import ShareTooltip from '../ShareTooltip';
import { VolumeControl } from '../VolumeControl';

interface PlayerControlsProps {
  contributorId?: string;
  slug?: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onTrackSelect?: (index: number) => void;
  playlistOpen?: boolean;
  volume?: number;
  onVolumeChange?: (volume: number) => void;
  isLoading?: boolean;
  isBuffering?: boolean;
  onMinimize?: () => void;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
}

export const PlayerControls = ({
  isPlaying,
  onPlayPause,
  onNext,
  onPrev,
  onTrackSelect,
  playlistOpen = false,
  volume = 1,
  onVolumeChange,
  isLoading = false,
  isBuffering = false,
  onMinimize,
  audioRef,
  contributorId,
  slug,
}: PlayerControlsProps) => {
  const { t } = useTranslation();
  const [isShareTooltipOpen, setIsShareTooltipOpen] = useState(false);
  const [isAddToPlaylistModalOpen, setIsAddToPlaylistModalOpen] =
    useState(false);
  const {
    activePlaylist,
    repeatMode,
    shuffleEnabled,
    shuffleOrder,
    setShuffleEnabled,
    setRepeatMode,
  } = useMusicState();
  const { setPlaylistOpen, setIsMinimized } = useCommonState();
  const { toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const { setCompanionSlug } = useSessionFeatureState();
  const { isAuthenticated, signinRedirect } = useAuth();
  const isStationMode = useStationMode();

  // Determine if previous/next buttons should be disabled
  const isFirstTrack = !activePlaylist || activePlaylist.currentIndex === 0;
  const isLastTrack =
    !activePlaylist ||
    activePlaylist.currentIndex >= activePlaylist.album.tracks.length - 1;

  // Next button should be enabled if:
  // 1. Not on last track, OR
  // 2. Repeat mode is 'all' or 'one', OR
  // 3. Shuffle is enabled (can continue in shuffle order)
  const isNextDisabled =
    !activePlaylist ||
    (isLastTrack &&
      repeatMode === 'off' &&
      (!shuffleEnabled || shuffleOrder.length === 0));

  // Previous button should be enabled if:
  // 1. Not on first track, OR
  // 2. Repeat mode is 'all' (can loop back to last track), OR
  // 3. Shuffle is enabled (can go back in shuffle order)
  const isPrevDisabled =
    !activePlaylist ||
    (isFirstTrack &&
      repeatMode !== 'all' &&
      (!shuffleEnabled || shuffleOrder.length === 0));

  const handleQueueToggle = useCallback(() => {
    setPlaylistOpen(!playlistOpen);
  }, [playlistOpen, setPlaylistOpen]);

  const handleAddToPlaylistClick = useCallback(() => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login...');
      const fullPath = window.location.pathname + window.location.search;
      signinRedirect({
        extraQueryParams,
        state: { returnUrl: fullPath },
      }).catch((error) => {
        console.error('Error during sign-in redirect:', error);
      });
      return;
    }
    // Open the modal if authenticated
    setIsAddToPlaylistModalOpen(true);
  }, [isAuthenticated, signinRedirect]);

  // Handler for downloading creation track
  const handleDownloadCreation = useCallback(() => {
    if (!activePlaylist || activePlaylist.album.category !== 'my-creations') {
      return;
    }
    const currentTrack =
      activePlaylist.album.tracks[activePlaylist.currentIndex];
    if (!currentTrack) return;

    try {
      const link = document.createElement('a');
      link.href = currentTrack.url || '';
      link.download = `${currentTrack.name || 'track'}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t('player.downloading', { name: currentTrack.name }));

      // Analytics: creation_downloaded
      const contributor = activePlaylist?.album?.contributors?.[0];
      const contentType = getContentType(activePlaylist?.album?.category);
      trackCreationDownloaded({
        creationId: currentTrack.id,
        creationType: contentType,
        creationName: currentTrack.name,
        creationCompanion: contributor?.givenName || 'Unknown',
      });
    } catch (error) {
      console.error('Error downloading track:', error);
      toast.error(t('player.downloadFailed'));
    }
  }, [activePlaylist]);

  const isShuffleDisabled = activePlaylist?.album.category === 'book';

  const handleShuffleToggle = useCallback(() => {
    if (isShuffleDisabled) return;
    setShuffleEnabled(!shuffleEnabled);
  }, [shuffleEnabled, setShuffleEnabled, isShuffleDisabled]);

  const handleRepeatToggle = useCallback(() => {
    switch (repeatMode) {
      case 'off':
        setRepeatMode('all');
        break;
      case 'all':
        setRepeatMode('one');
        break;
      case 'one':
        setRepeatMode('off');
        break;
      default:
        setRepeatMode('off');
    }
  }, [repeatMode, setRepeatMode]);

  const handleTrackSelect = useCallback(
    (index: number) => {
      if (onTrackSelect) {
        onTrackSelect(index);
      }
      // Keep playlist open when selecting a track
    },
    [onTrackSelect],
  );

  const handleShareClick = useCallback(async () => {
    // Check if we're on mobile and native Web Share API is available
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    if (navigator.share && isMobile && activePlaylist) {
      // Use native share API with 🎧 emoji for mobile
      const currentTrack =
        activePlaylist.album.tracks[activePlaylist.currentIndex];
      const artistName =
        activePlaylist.album.contributors[0]?.givenName || 'Unknown Artist';
      const shareText =
        activePlaylist.album.category === 'my-creations'
          ? t('player.shareCreation', { name: currentTrack?.name })
          : t('player.shareMusic', { track: currentTrack?.name, artist: artistName, album: activePlaylist.album.albumName });

      try {
        await navigator.share({
          url: window.location.href,
          title: `${currentTrack?.name} - ${artistName}`,
          text: shareText,
        });
        // Analytics: content_shared via Native Share
        const trackId = currentTrack?.id || '';
        const contentType = getContentType(activePlaylist.album.category);
        trackContentShared({
          contentId: trackId,
          contentType: contentType,
          contentName: currentTrack?.name || '',
          creatorName: artistName,
          shareDestination: SHARE_DESTINATIONS.NATIVE_SHARE,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to tooltip
        console.log('Native share cancelled, showing tooltip', err);
        setIsShareTooltipOpen((prev) => !prev);
      }
    } else {
      // Use tooltip on desktop or when native share not available
      setIsShareTooltipOpen((prev) => !prev);
    }
  }, [activePlaylist]);

  const handleConnect = async () => {
    if (!isAuthenticated) {
      const companionVideoUrl = currentContributor?.videoDesktopUrl;
      const companionImageUrl = currentContributor?.thumbnailUrl;

      signinRedirect({
        extraQueryParams: {
          ...extraQueryParams,
          ...(companionImageUrl && {
            backgroundImageUrl: companionImageUrl,
          }),
          ...(companionVideoUrl && { backgroundUrl: companionVideoUrl }),
        },
        state: { returnUrl: window.location.href },
      }).catch((error) => {
        console.error('Error during sign-in redirect:', error);
      });
      return;
    }

    // OLD CODE: Volume reductio
    // if (volume > 0.2 && onVolumeChange) {
    //   onVolumeChange(0.2);
    // }

    if (isStationMode) {
      if (!contributorId) {
        toast.error(t('player.companionFailed'));
        return;
      }

      try {
        const data = await getContributorById(contributorId);
        if (data?.companionId) {
          sendSelectCompanionMessage(data.companionId);
        } else {
          toast.error(
            t('player.companionFailed'),
          );
        }
      } catch (error) {
        console.error('Error fetching contributor:', error);
        toast.error(t('player.companionFailed'));
      }
      return;
    }

    if (slug && slug.trim() !== '' && !slug.includes(' ')) {
      setCompanionSlug({
        companionSlug: slug,
        isSessionPage: false,
        autoConnect: true,
      });
      return;
    }

    if (!contributorId) {
      toast.error(t('player.companionFailed'));
      return;
    }

    try {
      const data = await getContributorById(contributorId);
      if (data?.companionSlug) {
        setCompanionSlug({
          companionSlug: data.companionSlug,
          isSessionPage: false,
          autoConnect: true,
        });
      } else {
        toast.error(t('player.companionFailed'));
      }
    } catch (error) {
      console.error('Error fetching contributor:', error);
      toast.error(t('player.companionFailed'));
    }
  };

  // Handle contributor click - navigate to contributor profile and minimize player
  const handleContributorClick = useCallback(() => {
    const contributor = activePlaylist?.album?.contributors[0];
    if (contributor?.id) {
      // Minimize the player first (like sidebar navigation)
      setIsMinimized(true);
      // Then navigate to contributor profile
      navigate(`/contributor/${contributor.id}`);
    }
  }, [
    activePlaylist?.album?.contributors,
    navigate,
    setIsMinimized,
    isPlaying,
    onPlayPause,
  ]);

  const handleShareTooltipClose = useCallback(() => {
    setIsShareTooltipOpen(false);
  }, []);

  const isGeneratedTrackSaved = useCallback(() => {
    if (!activePlaylist || activePlaylist.album.category !== 'generated') {
      return true;
    }
    const trackId =
      activePlaylist.album.tracks[activePlaylist.currentIndex]?.id;
    if (!trackId) return false;
    const TEMP_ID_PREFIX = 'temp-generated-';
    return !trackId.startsWith(TEMP_ID_PREFIX);
  }, [
    activePlaylist?.album.category,
    activePlaylist?.currentIndex,
    activePlaylist?.album.tracks,
  ]);

  // Generate share URL for current track
  const generateShareUrl = useCallback(() => {
    if (!activePlaylist) return window.location.origin;

    const baseUrl = window.location.origin;
    const albumId = activePlaylist.album.id;
    const trackId =
      activePlaylist.album.tracks[activePlaylist.currentIndex]?.id;

    // Use different URL format based on album category
    if (activePlaylist.album.type === 'playlist') {
      return trackId
        ? `${baseUrl}/playlist/${albumId}/${trackId}`
        : window.location.origin;
    } else if (
      activePlaylist.album.category === 'my-creations' ||
      activePlaylist.album.category === 'generated'
    ) {
      const orgId = activePlaylist.album.orgId;
      return trackId && orgId
        ? `${baseUrl}/creations/track/${orgId}/${trackId}`
        : window.location.origin;
    }

    // Clean, simple share URL - meta tags will be injected by server/client
    return trackId
      ? `${baseUrl}/player/${albumId}/${trackId}`
      : window.location.origin;
  }, [
    activePlaylist?.album.id,
    activePlaylist?.album.category,
    activePlaylist?.album.orgId,
    activePlaylist?.currentIndex,
    activePlaylist?.album.tracks,
  ]);
  const activeTrack = activePlaylist
    ? activePlaylist.album.tracks[activePlaylist.currentIndex]
    : null;

  const currentContributor =
    activePlaylist?.album?.type === 'playlist'
      ? activeTrack?.contributors?.[0]
      : activePlaylist?.album?.contributors?.[0];
  return (
    <>
      {/* Queue Modal */}
      <PlaylistQueue
        isPlaying={isPlaying}
        onPlayPause={onPlayPause}
        onTrackSelect={handleTrackSelect}
        isLoading={isLoading}
        onMinimize={onMinimize}
        audioRef={audioRef}
      />
      {/* Add to My Playlists Modal (show for all except generated songs) */}
      {activeTrack && activePlaylist?.album.category !== 'generated' && (
        <AddToPlaylistModal
          isOpen={isAddToPlaylistModalOpen}
          onClose={() => setIsAddToPlaylistModalOpen(false)}
          trackId={activeTrack.id}
          trackName={activeTrack.name}
          creatorName={currentContributor?.givenName}
          category={
            // For creations (my-creations category or itemType='creation'), use 'music'
            // so creations are added to music playlists, not a separate creation category
            activePlaylist?.album.category === 'my-creations' ||
            activePlaylist?.album.category === 'creation' ||
            (activeTrack as any)?.itemType === 'creation'
              ? 'music'
              : (activePlaylist?.album.category as
                  | 'music'
                  | 'serenity'
                  | 'book'
                  | 'creation') || 'music'
          }
          // Pass creation-specific props when playing my-creations or creation playlists
          itemType={
            activePlaylist?.album.category === 'my-creations' ||
            activePlaylist?.album.category === 'creation' ||
            (activeTrack as any)?.itemType === 'creation'
              ? 'creation'
              : undefined
          }
          itemId={
            activePlaylist?.album.category === 'my-creations' ||
            activePlaylist?.album.category === 'creation' ||
            (activeTrack as any)?.itemType === 'creation'
              ? activeTrack.id
              : undefined
          }
          creationOrgId={
            activePlaylist?.album.category === 'my-creations'
              ? (activePlaylist?.album as any)?.orgId
              : activePlaylist?.album.category === 'creation' ||
                  (activeTrack as any)?.itemType === 'creation'
                ? (activeTrack as any)?.creationOrgId ||
                  (activeTrack as any)?.orgId
                : undefined
          }
        />
      )}
      {/* Mobile Controls - Vertical Right Aligned */}
      <div
        className={`absolute right-4 top-1/2 -translate-y-1/2 z-[11] ${
          isStationMode ? '' : 'sm:hidden'
        }`}>
        <div className="flex flex-col items-center gap-2">
          {/* Like Button - hide for creations (by category or itemType) */}
          {activePlaylist?.album.category !== 'my-creations' &&
            activePlaylist?.album.category !== 'creation' &&
            (activeTrack as any)?.itemType !== 'creation' && (
              <div
                className="flex w-[42px] h-[42px] rounded-full items-center cursor-pointer justify-center flex-col bg-black/50 backdrop-blur-sm z-[9999]"
                onClick={() =>
                  activeTrack?.id &&
                  toggleFavorite(
                    'track',
                    activeTrack?.id,
                    activeTrack.isLiked,
                    activeTrack.name,
                    currentContributor?.givenName,
                    activePlaylist?.album.category,
                  )
                }>
                <div className="w-4 h-4 flex items-center justify-center">
                  {activeTrack?.isLiked ? (
                    <Likedicon width={16} height={16} />
                  ) : (
                    <FavoriteIcon
                      fillOpacity={1}
                      fill="white"
                      width={16}
                      height={16}
                    />
                  )}
                </div>
                {/* <div className="flex text-[#FCFCFC] text-[8px] font-semibold">
                            57
                        </div> */}
              </div>
            )}

          {isGeneratedTrackSaved() && (
            <div className="relative">
              <button
                onClick={handleShareClick}
                className="flex w-[42px] h-[42px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center">
                  <ForwardIcon width={16} height={16} />
                </div>
              </button>
              {isShareTooltipOpen && (
                <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-black/90 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap">
                  {t('player.shareTrack')}
                  <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black/90 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                </div>
              )}
            </div>
          )}

          {/* Add to My Playlists Button (show for all except generated) */}
          {activePlaylist?.album.category !== 'generated' && (
            <>
              <Tooltip
                content={t('player.addToPlaylists')}
                anchor="#add-to-playlist-button-full"
                placement="top"
              />
              <button
                id="add-to-playlist-button-full"
                onClick={handleAddToPlaylistClick}
                className="flex w-[42px] h-[42px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center">
                  <Icon name="playlist-add" size={16} color="white" />
                </div>
              </button>
            </>
          )}

          {/* Download Button (only for my-creations) */}
          {activePlaylist?.album.category === 'my-creations' && (
            <>
              <Tooltip
                content={t('player.download')}
                anchor="#download-button-full"
                placement="top"
              />
              <button
                id="download-button-full"
                onClick={handleDownloadCreation}
                className="flex w-[42px] h-[42px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center">
                  <DownloadIcon width={16} height={16} color="white" />
                </div>
              </button>
            </>
          )}

          {/* Queue Button */}
          <>
            <Tooltip
              content={t('player.queue')}
              anchor="#queue-button-full"
              placement="top"
            />
            <button
              id="queue-button-full"
              onClick={handleQueueToggle}
              className="flex w-[42px] h-[42px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
              <div className="w-4 h-4 flex items-center justify-center">
                <PlaylistIcon width={13} height={13} />
              </div>
            </button>
          </>

          {/* Shuffle Button */}
          <>
            <Tooltip
              content={
                isShuffleDisabled
                  ? t('player.shuffleNotAvailable')
                  : shuffleEnabled
                    ? t('player.shuffleOn')
                    : t('player.shuffleOff')
              }
              anchor="#shuffle-button-mobile"
              placement="top"
            />
            <button
              id="shuffle-button-mobile"
              onClick={handleShuffleToggle}
              disabled={isShuffleDisabled}
              className={`flex w-[42px] h-[42px] rounded-full items-center justify-center transition-colors ${
                isShuffleDisabled
                  ? 'bg-black/50 backdrop-blur-sm text-white/30 cursor-not-allowed opacity-70'
                  : shuffleEnabled
                    ? 'bg-orange-500 text-black'
                    : 'bg-black/50 backdrop-blur-sm hover:bg-white/10 text-white/60'
              }`}>
              <Icon name="shuffle" size={20} color="currentColor" />
            </button>
          </>

          {/* Repeat Button */}
          <>
            <Tooltip
              content={
                repeatMode === 'one'
                  ? t('player.repeatOne')
                  : repeatMode === 'all'
                    ? t('player.repeatAll')
                    : t('player.repeatOff')
              }
              anchor="#repeat-button-full"
              placement="top"
            />
            <button
              id="repeat-button-full"
              onClick={handleRepeatToggle}
              className={`flex w-[42px] h-[42px] rounded-full items-center justify-center transition-colors relative ${
                repeatMode !== 'off'
                  ? 'bg-orange-500 text-black'
                  : 'bg-black/50 backdrop-blur-sm hover:bg-white/10 text-white/60'
              }`}>
              <Icon
                name={repeatMode === 'one' ? 'repeat-one' : 'repeat'}
                size={20}
                color="currentColor"
              />
            </button>
          </>

          {/* Language Selector */}
          {activeTrack && (
            <LanguageSelector
              size={24}
              className=" bg-black/50 backdrop-blur-sm rounded-full flex w-[42px] h-[42px] rounded-full items-center justify-center hover:bg-white/10 transition-colors"
            />
          )}

          {/* Volume Control */}
          {onVolumeChange && (
            <div
              className={`${
                isStationMode ? 'block' : 'hidden sm:block'
              } bg-black/50 backdrop-blur-sm rounded-full `}>
              <VolumeControl
                volume={volume}
                onVolumeChange={onVolumeChange}
                size={16}
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop Controls - Horizontal Bottom Aligned */}
      <div
        className={`absolute bottom-0 left-0 px-4 mb-[130px] z-[11] transition-all duration-300 ${
          isStationMode
            ? 'hidden'
            : `hidden sm:block ${
                playlistOpen && !isStationMode
                  ? 'sm:right-[384px]'
                  : 'sm:right-0'
              }`
        }`}>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            {/* Contributor Info */}
            {currentContributor && (
              <div className={`flex items-center gap-2 pt-2`}>
                <div
                  className="flex cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleContributorClick}
                  title={t('player.viewContributor')}>
                  <img
                    className={` w-[42px] h-[42px]  object-cover object-center rounded-full`}
                    src={
                      currentContributor.thumbnailUrl ||
                      placeHolderData.contributorThumbnail
                    }
                    alt={currentContributor.givenName}
                  />
                </div>
                <div
                  className="flex flex-col cursor-pointer hover:text-white/90 transition-colors"
                  onClick={handleContributorClick}
                  title={t('player.viewContributor')}>
                  <span
                    className={`font-medium text-md
                    } text-white/80`}>
                    {currentContributor.givenName}
                  </span>
                </div>
                {/* Only show Connect button if slug is valid OR contributorId is available */}
                {((slug && slug.trim() !== '' && !slug.includes(' ')) ||
                  (contributorId && contributorId.trim() !== '')) && (
                  <div className="flex pl-2">
                    <div onClick={handleConnect}>
                      <GradientButton icon="" title={t('player.connect')} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Center - Main Playback Controls (Prev/Play/Next) */}
          <div className="flex items-center gap-4">
            <button
              onClick={onPrev}
              disabled={isPrevDisabled}
              className={`flex w-[40px] h-[40px] rounded-full items-center justify-center transition-colors ${
                isPrevDisabled
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-white/10'
              }`}>
              <PrevIcon />
            </button>

            {/* PLAY / PAUSE - Main Button */}
            <button
              onClick={onPlayPause}
              disabled={isBuffering}
              className={`flex w-[50px] h-[50px] rounded-full items-center justify-center bg-white/10 hover:bg-white/20 transition-colors ${
                isBuffering ? 'cursor-wait' : ''
              }`}>
              {isBuffering ? (
                <NapsterSpinner size="small" />
              ) : isPlaying ? (
                <PauseIcon />
              ) : (
                <PlayIcon width={24} height={25} />
              )}
            </button>

            <button
              onClick={onNext}
              disabled={isNextDisabled}
              className={`flex w-[40px] h-[40px] rounded-full items-center justify-center transition-colors ${
                isNextDisabled
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-white/10'
              }`}>
              <PlayNextIcon />
            </button>
          </div>

          {/* Right Side - Secondary Controls */}
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            {activeTrack && (
              <LanguageSelector
                size={24}
                className="flex w-[40px] h-[40px] rounded-full items-center justify-center hover:bg-white/10 transition-colors"
              />
            )}

            {/* Like Button - hide for creations (by category or itemType) */}
            {activePlaylist?.album.category !== 'my-creations' &&
              activePlaylist?.album.category !== 'creation' &&
              (activeTrack as any)?.itemType !== 'creation' && (
                <>
                  <Tooltip
                    content={activeTrack?.isLiked ? t('player.unlike') : t('player.like')}
                    anchor="#favorite-button-small"
                    placement="top"
                  />
                  <div
                    id="favorite-button-small"
                    className="flex w-[40px] h-[40px] cursor-pointer rounded-full items-center justify-center flex-col z-[999] hover:bg-white/10"
                    onClick={() =>
                      activeTrack?.id &&
                      toggleFavorite(
                        'track',
                        activeTrack?.id,
                        activeTrack.isLiked,
                        activeTrack.name,
                        currentContributor?.givenName,
                        activePlaylist?.album.category,
                      )
                    }>
                    {activeTrack?.isLiked ? (
                      <Likedicon width={24} height={27} />
                    ) : (
                      <FavoriteIcon
                        fillOpacity={1}
                        fill="white"
                        width={24}
                        height={27}
                      />
                    )}
                  </div>
                </>
              )}
            {/* Add to My Playlists Button (show for all except generated) */}
            {activePlaylist?.album.category !== 'generated' && (
              <>
                <Tooltip
                  content={t('player.addToPlaylists')}
                  anchor="#add-to-playlist-button-small"
                  placement="top"
                />
                <button
                  id="add-to-playlist-button-small"
                  onClick={handleAddToPlaylistClick}
                  className="flex w-[40px] h-[40px] rounded-full items-center justify-center transition-colors hover:bg-white/10">
                  <Icon name="playlist-add" size={24} color="white" />
                </button>
              </>
            )}

            {/* Download Button (only for my-creations) */}
            {activePlaylist?.album.category === 'my-creations' && (
              <>
                <Tooltip
                  content={t('player.download')}
                  anchor="#download-button-small"
                  placement="top"
                />
                <button
                  id="download-button-small"
                  onClick={handleDownloadCreation}
                  className="flex w-[40px] h-[40px] rounded-full items-center justify-center transition-colors hover:bg-white/10">
                  <DownloadIcon width={24} height={24} color="white" />
                </button>
              </>
            )}

            {isGeneratedTrackSaved() && (
              <div className="relative">
                <>
                  <Tooltip
                    content={t('player.share')}
                    anchor="#share-button-small"
                    placement="top"
                  />
                  <button
                    id="share-button-small"
                    onClick={handleShareClick}
                    className={`flex w-[40px] h-[40px] rounded-full items-center justify-center transition-colors hover:bg-white/10`}>
                    <ForwardIcon width={24} height={24} />
                  </button>
                </>

                {/* Share Tooltip */}
                {activePlaylist && (
                  <ShareTooltip
                    key={generateShareUrl()}
                    isOpen={isShareTooltipOpen}
                    onClose={handleShareTooltipClose}
                    trackName={
                      activePlaylist.album.tracks[activePlaylist.currentIndex]
                        ?.name || 'Unknown Track'
                    }
                    albumName={activePlaylist.album.albumName}
                    artistName={
                      activePlaylist.album.contributors[0]?.givenName ||
                      'Unknown Artist'
                    }
                    shareUrl={generateShareUrl()}
                    category={activePlaylist.album.category}
                  />
                )}
              </div>
            )}
            <>
              <Tooltip
                content={
                  activePlaylist?.album.category === 'book'
                    ? t('player.shuffleNotAvailable')
                    : shuffleEnabled
                      ? t('player.shuffleOn')
                      : t('player.shuffleOff')
                }
                anchor="#shuffle-button-minimized"
                placement="top"
              />
              <button
                id="shuffle-button-minimized"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (activePlaylist?.album.category !== 'book') {
                    handleShuffleToggle();
                  }
                }}
                disabled={activePlaylist?.album.category === 'book'}
                className={`p-2 rounded-full transition-colors ${
                  activePlaylist?.album.category === 'book'
                    ? 'text-white cursor-not-allowed opacity-70'
                    : shuffleEnabled
                      ? 'bg-orange-500 text-black'
                      : 'hover:bg-white/10 text-white'
                }`}
                type="button">
                <Icon name="shuffle" size={24} color="currentColor" />
              </button>
            </>

            {/* Repeat */}
            <>
              <Tooltip
                content={
                  repeatMode === 'off'
                    ? t('player.repeatOff')
                    : repeatMode === 'all'
                      ? t('player.repeatAll')
                      : t('player.repeatOne')
                }
                anchor="#repeat-button-small"
                placement="top"
              />
              <button
                id="repeat-button-small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRepeatToggle();
                }}
                className={`p-2 rounded-full transition-colors relative ${
                  repeatMode !== 'off'
                    ? 'bg-orange-500 text-black'
                    : 'hover:bg-white/10 text-white'
                }`}
                type="button">
                <Icon
                  name={repeatMode === 'one' ? 'repeat-one' : 'repeat'}
                  size={24}
                  color="currentColor"
                />
              </button>
            </>
            {/* Volume Control */}
            {onVolumeChange && (
              <VolumeControl
                volume={volume}
                onVolumeChange={onVolumeChange}
                size={24}
              />
            )}

            {/* Queue Button */}
            <>
              <Tooltip
                content={t('player.queue')}
                anchor="#queue-button-small"
                placement="top"
              />
              <button
                id="queue-button-small"
                onClick={handleQueueToggle}
                className="flex w-[40px] h-[40px] rounded-full items-center justify-center hover:bg-white/10 transition-colors">
                <PlaylistIcon />
              </button>
            </>
          </div>
        </div>
      </div>
    </>
  );
};
